import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import GrubToastMessage from "../../../components/alert/GrubToastMessage";
import * as Constants from "../../../constants/Constants";
import { validEmailRegex } from "../../../helpers/utils/Utils";
import { GrubPopupErrorHook } from "../../../hooks/index";
import { userSignIn } from "../services/authServices";

export const useSignIn = (props) => {
    //Hooks
    const { error } = GrubPopupErrorHook();
    const { addToast } = GrubToastMessage();

    //States
    const [isFocused, setIsFocused] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [keyboardOpen, setKeyboardOpen] = useState(false);

    // Refs
    const isSubmittingRef = useRef(false);
    const emailRef = useRef();

    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .trim()
                .matches(Constants.EMAIL_REGEX, Constants.USER_EMAIL_NOT_VALID)
                .required(Constants.USER_EMAIL_REQUIRED)
                .max(
                    255,
                    Constants.USER_EMAIL_LESS_THEN_OR_EQUAL_255_CHARACTER
                ),
            password: Yup.string()
                .min(8, Constants.PASSWORD_MUST_BE_8_CHARACHTER)
                .required(Constants.PASSWORD_FIELD_REQUIRED),
        }),
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        if (props.isOpen) {
            setIsFocused(true);
        }

        const onResize = () => {
            if (window.visualViewport.height < window.innerHeight) {
                setKeyboardOpen(true);
            } else {
                setKeyboardOpen(false);
            }
        };

        window.visualViewport.addEventListener("resize", onResize);
        return () =>
            window.visualViewport.removeEventListener("resize", onResize);
    }, [props.isOpen]);

    // Check if form has values (not validation)
    const hasFormValues = () => {
        return (
            formik.values.email.trim() !== "" &&
            formik.values.password.trim() !== ""
        );
    };

    async function handleSubmit(values, { setSubmitting }) {
        // Validate form before submission
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            formik.setTouched({
                email: true,
                password: true,
            });
            return;
        }

        setLoading(true);

        try {
            const response = await userSignIn({
                email: values.email.trim(),
                password: values.password.trim(),
            });            

            if (response.data.flag === true) {
                addToast({
                    alertStatus: Constants.TOAST_TYPE_SUCESS,
                    alertTitle: Constants.USER_LOGING,
                    alertDescription: response.data.message,
                });
                props.userLogin(response.data.data.user[0]);
                localStorage.setItem("token", response.data.data.token);
                props.handleBackButton(true);
                formik.resetForm();
                setErrorMessage({});
            }
        } catch (err) {
            const popupError = error({ actionData: err });
            if (
                popupError.message ===
                "Sorry, user does not exist. Please contact to administrator."
            ) {
                props.handleSignUpOpen(popupError);
            } else if (popupError.error) {
                setErrorMessage(popupError);
                setTimeout(() => setErrorMessage({}), 5000);
            }
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    }

    const handleKeyDown = (e, nextRef) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (nextRef === "submit") {
                if (hasFormValues() && !loading) {
                    formik.handleSubmit();
                }
            } else {
                const input = document.getElementById(nextRef);
                if (input) input.focus();
            }
        }
    };

    return {
        handleKeyDown,
        formik,
        isSubmittingRef,
        isFocused,
        emailRef,
        errorMessage,
        loading,
        keyboardOpen,
    };
};
